import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import { go } from '@platform/router.actions';
import { RouteGuard } from '@platform/routing/route.guard';
import * as CoreActions from '@core/core.actions';
import { selectActiveUsers, selectAdminCustomerSpace } from '@core/core.selectors';
import { firstValueFrom } from 'rxjs';
import { ResourceStatuses } from '@platform/shared/resource.state';

@Injectable({
  providedIn: 'root',
})
export class CustomerSpaceAdminGuard implements RouteGuard {
  constructor(private store$: Store<RootState>) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForCustomerSpace(route);
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForCustomerSpace(childRoute.parent!);
  }

  private async loadAndWaitForCustomerSpace(route: ActivatedRouteSnapshot): Promise<boolean> {
    const customerSpaceId = route.parent!.paramMap.get('customerSpaceId');

    if (!customerSpaceId) {
      // create mode
      this.store$.dispatch(CoreActions.resetCustomerSpaceAdmin());
      this.store$.dispatch(CoreActions.loadActiveUsers());

      const activeUsersState = await firstValueFrom(
        this.store$.pipe(
          select(selectActiveUsers),
          first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        ),
      );

      const canActivate = activeUsersState.status === ResourceStatuses.SUCCEEDED;
      if (!canActivate) {
        this.store$.dispatch(go({ path: ['error'] }));
      }

      return canActivate;
    } else {
      // update mode
      this.store$.dispatch(CoreActions.loadCustomerSpaceAdmin({ customerSpaceId }));
      this.store$.dispatch(CoreActions.loadActiveUsers());

      const customerSpaceState = await firstValueFrom(
        this.store$.pipe(
          select(selectAdminCustomerSpace),
          first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        ),
      );

      const activeUsersState = await firstValueFrom(
        this.store$.pipe(
          select(selectActiveUsers),
          first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        ),
      );

      const canActivate =
        customerSpaceState.status === ResourceStatuses.SUCCEEDED && activeUsersState.status === ResourceStatuses.SUCCEEDED;

      if (!canActivate) {
        this.store$.dispatch(go({ path: ['error'] }));
      }

      return canActivate;
    }
  }
}
